/**
 * Куда вести человека по нажатию на push-уведомление. Чистая функция — без
 * плагинов, чтобы проверять тестами (src/__tests__/push.test.ts).
 *
 * Что присылает наш Chatwoot (Notification::PushNotificationService#fcm_data,
 * _vendor/chatwoot/app/services/notification/push_notification_service.rb):
 *   data.payload = JSON-строка { data: { notification: {
 *     id, notification_type, primary_actor_id, primary_actor_type,
 *     primary_actor: { id, conversation_id } } } }
 * У «Conversation» primary_actor.id — ВИДИМЫЙ номер переписки (display_id),
 * у «Message» видимый номер лежит в primary_actor.conversation_id.
 * 🪤 Номера кабинета в уведомлении нет (штатное приложение Chatwoot тоже его не
 * знает и берёт текущий кабинет). Берём account_id, если сервер его когда-нибудь
 * добавит, иначе — кабинет активного проекта.
 *
 * Свои уведомления UNIA (лента ГиперТаска / CRM — ещё не подключены) договоримся
 * слать так: data.route = готовый путь внутри Saleus («/pipeline/deal/<id>»),
 * либо data.deal_id. Путь принимаем только из белого списка разделов.
 */

export type PushData = Record<string, unknown> | null | undefined;

interface ChatwootPushNotification {
  notification_type?: string;
  primary_actor_type?: string;
  primary_actor_id?: number | string;
  primary_actor?: { id?: number | string; conversation_id?: number | string };
  account_id?: number | string;
}

/** Разделы, куда уведомлению можно вести. Всё прочее — во «Входящие». */
const ALLOWED_ROUTE = /^\/(inbox(\/\d+\/\d+)?|pipeline(\/deal\/[A-Za-z0-9-]+)?|calls|calendar)$/;

const positiveInt = (v: unknown): number | null => {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : null;
};

function parseJson(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  try { return JSON.parse(v); } catch { return null; }
}

/** Достаёт уведомление Chatwoot из data (FCM v1: payload; старый формат: notification). */
export function chatwootNotificationOf(data: PushData): ChatwootPushNotification | null {
  if (!data) return null;
  const payload = parseJson(data.payload) as { data?: { notification?: unknown } } | null;
  const fromV1 = payload?.data?.notification;
  const n = (fromV1 ?? parseJson(data.notification)) as ChatwootPushNotification | null;
  return n && typeof n === 'object' ? n : null;
}

/**
 * Путь внутри приложения или null (уведомление не наше / не разобрали —
 * тогда просто открываем приложение, никуда не переходя).
 */
export function pushToRoute(data: PushData, fallbackAccountId: number | null): string | null {
  if (!data) return null;

  // 1) Свои уведомления UNIA.
  if (typeof data.route === 'string' && ALLOWED_ROUTE.test(data.route)) return data.route;
  const dealId = typeof data.deal_id === 'string' || typeof data.deal_id === 'number' ? String(data.deal_id) : '';
  if (dealId && /^[A-Za-z0-9-]+$/.test(dealId)) return `/pipeline/deal/${dealId}`;

  // 2) Chatwoot.
  const n = chatwootNotificationOf(data);
  if (!n) return null;
  let displayId: number | null = null;
  if (n.primary_actor_type === 'Conversation') displayId = positiveInt(n.primary_actor?.id);
  else if (n.primary_actor_type === 'Message') displayId = positiveInt(n.primary_actor?.conversation_id);
  const account = positiveInt(n.account_id ?? data.account_id) ?? fallbackAccountId;
  if (displayId && account) return `/inbox/${account}/${displayId}`;
  return '/inbox';
}
